import {
  AVAILABLE_READING_FORMS,
  READING_APPETITES,
  READING_ARTICULATIONS,
  READING_BY_ID,
  READING_COMMITMENTS,
  READING_DIFFICULTIES,
  READING_INTENTS,
} from "../content/catalog";
import {
  BOUNDARY_STUDY,
  EXPRESSION_STUDY_BY_ID,
} from "../content/expression-studies";
import type {
  ReadingAppetite,
  ReadingArticulation,
  ReadingCommitment,
  ReadingForm,
  ReadingIntent,
  ReadingLexicalDifficulty,
} from "../content/types";

export type ShareableView = "home" | "read" | "practice";
export type ShareablePracticeView = "words" | "expression";

export type ShareableLocation = {
  view: ShareableView;
  workId: string | null;
  browse: boolean;
  particular: boolean;
  commitment: ReadingCommitment;
  appetite: ReadingAppetite;
  difficulty: ReadingLexicalDifficulty | null;
  intent: ReadingIntent | null;
  form: ReadingForm | null;
  articulation: ReadingArticulation | null;
  practiceView: ShareablePracticeView;
  studyId: string;
  stanceId: string;
};

export const DEFAULT_SHAREABLE_LOCATION: ShareableLocation = {
  view: "home",
  workId: null,
  browse: false,
  particular: false,
  commitment: "brief",
  appetite: "surprise",
  difficulty: null,
  intent: null,
  form: null,
  articulation: null,
  practiceView: "words",
  studyId: BOUNDARY_STUDY.id,
  stanceId: "tactful",
};

export const SHAREABLE_QUERY_KEYS = [
  "view",
  "work",
  "browse",
  "particular",
  "time",
  "mood",
  "difficulty",
  "intent",
  "form",
  "landing",
  "practice",
  "study",
  "stance",
] as const;

function oneOf<T extends string>(
  value: string | null,
  options: readonly T[],
  fallback: T,
) {
  return value && options.includes(value as T) ? value as T : fallback;
}

function optionalOneOf<T extends string>(
  value: string | null,
  options: readonly T[],
) {
  return value && options.includes(value as T) ? value as T : null;
}

export function parseShareableLocation(href: string): ShareableLocation {
  const url = new URL(href, "https://verbalore.com/");
  const legacyView = url.hash === "#read"
    ? "read"
    : url.hash === "#practice"
      ? "practice"
      : "home";
  const view = oneOf(
    url.searchParams.get("view"),
    ["home", "read", "practice"] as const,
    legacyView,
  );
  const work = url.searchParams.get("work");
  const studyId = oneOf(
    url.searchParams.get("study"),
    [...EXPRESSION_STUDY_BY_ID.keys()],
    BOUNDARY_STUDY.id,
  );
  const study = EXPRESSION_STUDY_BY_ID.get(studyId) || BOUNDARY_STUDY;

  return {
    view,
    workId: work && READING_BY_ID.has(work) ? work : null,
    browse: view === "read" && url.searchParams.get("browse") === "1",
    particular:
      view === "read"
      && url.searchParams.get("browse") === "1"
      && url.searchParams.get("particular") === "1",
    commitment: oneOf(
      url.searchParams.get("time"),
      READING_COMMITMENTS.map(({ id }) => id),
      "brief",
    ),
    appetite: oneOf(
      url.searchParams.get("mood"),
      READING_APPETITES.map(({ id }) => id),
      "surprise",
    ),
    difficulty: optionalOneOf(
      url.searchParams.get("difficulty"),
      READING_DIFFICULTIES.map(({ id }) => id),
    ),
    intent: optionalOneOf(
      url.searchParams.get("intent"),
      READING_INTENTS.map(({ id }) => id),
    ),
    form: optionalOneOf(url.searchParams.get("form"), AVAILABLE_READING_FORMS),
    articulation: optionalOneOf(
      url.searchParams.get("landing"),
      READING_ARTICULATIONS.map(({ id }) => id),
    ),
    practiceView: oneOf(
      url.searchParams.get("practice"),
      ["words", "expression"] as const,
      "words",
    ),
    studyId,
    stanceId: oneOf(
      url.searchParams.get("stance"),
      study.stances.map(({ id }) => id),
      study.stances.some(({ id }) => id === "tactful")
        ? "tactful"
        : study.stances[0]?.id || "tactful",
    ),
  };
}

export function buildShareableHref(
  href: string,
  state: ShareableLocation,
) {
  const url = new URL(href, "https://verbalore.com/");
  for (const key of SHAREABLE_QUERY_KEYS) url.searchParams.delete(key);
  url.hash = "";

  if (state.view === "read") {
    url.searchParams.set("view", "read");
    if (state.workId && READING_BY_ID.has(state.workId)) {
      url.searchParams.set("work", state.workId);
    }
    if (state.browse) url.searchParams.set("browse", "1");
    if (state.browse && state.particular) {
      url.searchParams.set("particular", "1");
    }
    url.searchParams.set("time", state.commitment);
    url.searchParams.set("mood", state.appetite);
    if (state.difficulty) url.searchParams.set("difficulty", state.difficulty);
    if (state.intent) url.searchParams.set("intent", state.intent);
    if (state.form) url.searchParams.set("form", state.form);
    if (state.articulation) url.searchParams.set("landing", state.articulation);
  } else if (state.view === "practice") {
    url.searchParams.set("view", "practice");
    url.searchParams.set("practice", state.practiceView);
    if (state.practiceView === "expression") {
      url.searchParams.set("study", state.studyId);
      url.searchParams.set("stance", state.stanceId);
    }
  }

  return `${url.pathname}${url.search}`;
}
